import moment from 'moment'
import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators'

import { capturePosthogEvent } from 'utils/posthog'

import { selectActualLanguages, selectActualTimezone } from 'containers/App/selectors'
import { CAMPAIGN_TYPES_VALUES, DELIVERY } from 'containers/AwarenessCenter/constants'
import { selectCampaignsSettings } from 'containers/AwarenessCenter/selectors'
import { shareReset } from 'containers/Share/actions'
import { addToast } from 'containers/Toasts/actions'

import {
  approveCampaignError,
  approveCampaignSuccess,
  removeDraftError,
  removeDraftSuccess,
  saveAndApproveCampaignError,
  saveAndApproveCampaignSuccess,
  saveAndTestCampaignError,
  saveAndTestCampaignSuccess,
  saveDraftError,
  saveDraftSuccess,
} from '../steps/actions'
import {
  REMOVE_DRAFT_ASYNC,
  SAVE_AND_APPROVE_CAMPAIGN_ASYNC,
  SAVE_AND_TEST_CAMPAIGN_ASYNC,
  SAVE_DRAFT_ASYNC,
} from '../steps/constants'
import { buildManagerNotificationSavePayload } from '../steps/managerNotificationUtils'
import { sendTryItOutError, sendTryItOutSuccess } from '../steps/ReviewAndLaunchStep/actions'
import { selectTestCampaignData } from '../steps/ReviewAndLaunchStep/selectors'
import { selectSendCompletionEmail, selectSendManagerReportEveryXDays } from '../steps/selectors'
import { selectSimulationSendingRemindersCount } from '../steps/SetupStep/selectors'
import { getProviderTag } from '../steps/TrainingModuleStep/constants'
import { MAX_TRAINING_REMINDERS } from '../WizardSimulationCampaign/constants'

import {
  SYNC_PLAYLIST_ASSIGNMENT_ASYNC,
  SYNC_PLAYLIST_REMINDERS_ASYNC,
  TRAINING_EVENTS,
} from './constants'
import { getPlaylistFieldError, getTrainingIds, isPlaylistSelection } from './playlist'

const UNAVAILABLE_REMOVED_NOTICE = 'Unavailable videos were removed from the saved draft.'

const selectTrainingCampaign = (state) => state.awarenessCenter.wizardTrainingCampaign.self.campaign

const buildLaunchProperties = (campaign) => {
  const videos = campaign.selectedTrainingVideo
  const providers = [
    ...new Set(videos.map(({ vendor }) => getProviderTag(vendor)?.label).filter(Boolean)),
  ].sort()

  return {
    trn_format: isPlaylistSelection(videos) ? 'playlist' : 'single',
    trn_campaign_type: campaign.triggerBasedCampaignData ? 'new_hire' : 'scheduled',
    trn_video_count: videos.length,
    trn_total_duration_sec: videos.reduce((total, { items }) => {
      const item = items.find(({ localeId }) => localeId === campaign.defaultLocaleId) || items[0]

      return total + (Number(item?.duration) || 0)
    }, 0),
    trn_provider_count: providers.length,
    trn_providers: providers,
  }
}

const mapSaveSuccessCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftSuccess,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignSuccess,
  [SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE]: saveAndTestCampaignSuccess,
}
const mapSaveErrorCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftError,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignError,
  [SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE]: saveAndTestCampaignError,
}

const removeDraftEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REMOVE_DRAFT_ASYNC.INITIATE),
    filter(() => {
      if (state$.value.awarenessCenter.wizardTrainingCampaign) return true
      return false
    }),
    mergeMap(() =>
      api.awarenessCenter
        .deleteCampaign(state$.value.awarenessCenter.wizardTrainingCampaign.self.campaign.id)
        .pipe(
          mergeMap(({ response }) => of(removeDraftSuccess(response))),
          catchError((error) => of(epicsErrorHandler(removeDraftError, error))),
          takeUntil(action$.pipe(ofType(REMOVE_DRAFT_ASYNC.CANCEL)))
        )
    )
  )

const saveDraftEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      SAVE_DRAFT_ASYNC.INITIATE,
      SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE,
      SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE
    ),
    filter(() => {
      if (state$.value.awarenessCenter.wizardTrainingCampaign) return true
      return false
    }),
    mergeMap((action) => {
      const campaignData = state$.value.awarenessCenter.wizardTrainingCampaign.self.campaign
      const trainingIds = getTrainingIds(campaignData.selectedTrainingVideo)

      const sendingRemindersCount = selectSimulationSendingRemindersCount(state$.value)
      const remindersCount =
        sendingRemindersCount > MAX_TRAINING_REMINDERS
          ? MAX_TRAINING_REMINDERS
          : sendingRemindersCount
      const trainingReminders =
        remindersCount && campaignData.trainingReminders.length
          ? Array(remindersCount)
              .fill(null)
              .map((_, index) => campaignData.trainingReminders[index])
          : []
      const { managerNotificationBanner, managerReportEmail } = buildManagerNotificationSavePayload(
        state$.value
      )
      const triggerBasedCampaignData = campaignData.triggerBasedCampaignData
      const isTriggerBasedCampaign = !!triggerBasedCampaignData
      const timezone = isTriggerBasedCampaign
        ? selectActualTimezone(state$.value)
        : campaignData.timezone
      const SCHEDULE_FORMAT = 'YYYY-MM-DDTHH:mm'
      const scheduleTime = triggerBasedCampaignData
        ? moment(`${campaignData.launchDate} 09:00`).format(SCHEDULE_FORMAT)
        : moment(`${campaignData.launchDate} ${campaignData.launchTime}`).format(SCHEDULE_FORMAT)

      return api.wizardCampaign
        .saveCampaign(state$.value.partnerOptions.selectedCompany?.value, {
          campaignId: campaignData.id,
          flowType: CAMPAIGN_TYPES_VALUES.TRAINING,
          name: campaignData.name,
          defaultLocaleId: campaignData.defaultLocaleId,
          participantsChoices: campaignData.participants.included,
          participantsExcludeChoices: campaignData.participants.excluded,
          timezone,
          scheduleTime,
          trainingIds,
          deliveryType: DELIVERY.EMAIL,
          trainingCampaignData: campaignData.messageStep.fields,
          sendReminderEveryXBusinessDays: campaignData.sendReminderEveryXBusinessDays,
          saveMailSignature: campaignData.saveMailSignature,
          closeTime: moment(campaignData.closeTime).format('YYYY-MM-DDTHH:mm[Z]'),
          sendRemindersUntil: isTriggerBasedCampaign ? null : campaignData.sendRemindersUntil,
          campaignTemplateId: campaignData.campaignTemplateId,
          sendCampaignSummaryToCampaignReportRecipients: isTriggerBasedCampaign
            ? false
            : campaignData.sendCampaignSummaryToCampaignReportRecipients,
          sendCompletionEmail: campaignData.sendCompletionEmail,
          trainingReminders,
          managerReportRemindersThreshold: campaignData.managerReportRemindersThreshold,
          sendManagerReportEveryXDays: campaignData.sendManagerReportEveryXDays,
          managerReportDayOfWeek: campaignData.managerReportDayOfWeek,
          managerNotificationBanner,
          managerReportEmail,
          maxRemindersPerParticipant: isTriggerBasedCampaign
            ? campaignData.maxRemindersPerParticipant
            : null,
          triggerBasedCampaignData: isTriggerBasedCampaign ? triggerBasedCampaignData : null,
          completionNotificationSettings: isTriggerBasedCampaign
            ? campaignData.completionNotificationSettings
            : null,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              mapSaveSuccessCallbacks[action.type](response, action),
              ...(action.type === SAVE_DRAFT_ASYNC.INITIATE &&
              campaignData.selectedTrainingVideo.some(({ is_deleted: isDeleted }) => isDeleted)
                ? [addToast({ message: UNAVAILABLE_REMOVED_NOTICE })]
                : [])
            )
          ),
          catchError((error) => {
            const failure = epicsErrorHandler(mapSaveErrorCallbacks[action.type], error)
            const playlistMessage = getPlaylistFieldError(error.response)

            return of(
              playlistMessage
                ? { ...failure, error: { ...failure.error, detail: playlistMessage } }
                : failure
            )
          }),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    })
  )

const approveCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_AND_APPROVE_CAMPAIGN_ASYNC.SUCCESS),
    filter(() => {
      if (state$.value.awarenessCenter.wizardTrainingCampaign) return true
      return false
    }),
    mergeMap(() =>
      api.wizardCampaign
        .approveCampaign(state$.value.awarenessCenter.wizardTrainingCampaign.self.campaign.id)
        .pipe(
          mergeMap(({ response }) => of(approveCampaignSuccess(response))),
          tap(() =>
            capturePosthogEvent(
              TRAINING_EVENTS.CAMPAIGN_LAUNCHED,
              buildLaunchProperties(selectTrainingCampaign(state$.value))
            )
          ),
          catchError((error) => of(epicsErrorHandler(approveCampaignError, error))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    )
  )

const sendTryItOutEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_AND_TEST_CAMPAIGN_ASYNC.SUCCESS),
    filter(() => {
      if (state$.value.awarenessCenter.wizardTrainingCampaign) return true
      return false
    }),
    mergeMap((action) => {
      const testCampaignData = selectTestCampaignData(state$.value)
      const sendCompletionEmail = selectSendCompletionEmail(state$.value)
      const sendManagerReportEveryXDays = selectSendManagerReportEveryXDays(state$.value)

      return api.wizardCampaign
        .postTryItOut({
          campaignId: state$.value.awarenessCenter.wizardTrainingCampaign.self.campaign.id,
          profileIds: testCampaignData.profileIds,
          scenarios: action.payload.initialAction.payload.scenarios,
          trainingReminderIndices: testCampaignData.includeTrainingReminders
            ? testCampaignData.trainingReminderIndices
                .filter((index) => typeof index === 'number')
                .map((index) => index + 1)
            : undefined,
          sendNotifyManagerEmail: sendManagerReportEveryXDays
            ? testCampaignData.sendNotifyManagerEmail
            : undefined,
          sendTrainingCompletionEmail: sendCompletionEmail
            ? testCampaignData.sendTrainingCompletionEmail
            : undefined,
          sendSimulationFailureEmail: testCampaignData.sendSimulationFailureEmail,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              sendTryItOutSuccess(response),
              addToast({ message: 'Test campaign was sent successfully!' }),
              shareReset()
            )
          ),
          catchError((error) => of(epicsErrorHandler(sendTryItOutError, error))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    })
  )

const assignmentLocaleKey = (state, localeId) => {
  const languages = selectActualLanguages(state)

  return Array.isArray(languages)
    ? languages.find((language) => language.id === localeId)?.short_name?.toLowerCase()
    : undefined
}

// Both assignment wordings are already on the tenant settings. The reducer keeps a body
// the admin edited and replaces one that still equals the other variant.
const syncPlaylistAssignmentEpic = (action$, state$) =>
  action$.pipe(
    ofType(SYNC_PLAYLIST_ASSIGNMENT_ASYNC.INITIATE),
    map(() => {
      const state = state$.value
      const settings = selectCampaignsSettings(state)?.data
      const campaign = selectTrainingCampaign(state)
      const shortName = assignmentLocaleKey(state, campaign.defaultLocaleId)
      const single = settings?.defaultTrainingAssignmentMessage?.[shortName]?.body
      const playlist = settings?.defaultTrainingAssignmentMessagePlaylist?.[shortName]?.body
      const isPlaylist = isPlaylistSelection(campaign.selectedTrainingVideo)

      return SYNC_PLAYLIST_ASSIGNMENT_ASYNC.onSuccess({
        previousDefault: isPlaylist ? single : playlist,
        nextDefault: isPlaylist ? playlist : single,
      })
    })
  )

// Apply the selected wording from the tenant settings already loaded by Awareness Center.
// The reducer replaces only untouched reminders and keeps custom content intact.
const syncPlaylistRemindersEpic = (action$, state$) =>
  action$.pipe(
    ofType(SYNC_PLAYLIST_REMINDERS_ASYNC.INITIATE),
    map(({ payload }) => {
      const settings = selectCampaignsSettings(state$.value).data
      return SYNC_PLAYLIST_REMINDERS_ASYNC.onSuccess({
        previousDefaults:
          selectTrainingCampaign(state$.value).reminderDefaults ??
          settings?.defaultTrainingReminderContentTraining,
        nextDefaults: payload.trainingIds
          ? settings?.defaultTrainingReminderContentPlaylist
          : settings?.defaultTrainingReminderContentTraining,
      })
    })
  )

export default combineEpics(
  combineEpics(
    saveDraftEpic,
    removeDraftEpic,
    sendTryItOutEpic,
    approveCampaignEpic,
    syncPlaylistAssignmentEpic,
    syncPlaylistRemindersEpic
  )
)
